import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft, IconPlus, IconTrash } from "@tabler/icons-react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useChildren, initials } from "@/pages/app/circulo/hooks";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

export default function CirculoAdmin() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const { items, loading, reload } = useChildren();
  const [metrics, setMetrics] = useState({ active_cases: 0, pending_reports: 0, alerts: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/circulo/admin/metrics");
        setMetrics(data);
      } catch { /* ignore */ }
    })();
  }, [items]);

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="ca-back">
          <IconArrowLeft size={13} /> {t("app.back")}
        </button>
        <span className="t">CÍRCULO · ADMINISTRADORA</span>
      </div>
      <div className="wrap-app py-10 max-w-[860px]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="eyebrow">{lang === "es" ? "Panel de Mariela" : "Mariela's panel"}</p>
            <h1 className="font-display font-medium text-[26px] mt-1">
              {lang === "es" ? "Casos del círculo" : "Circle cases"}
            </h1>
          </div>
          <button className="btn" onClick={() => nav("/app/circulo/nuevo")} data-testid="admin-new-case">
            <IconPlus size={13} /> {lang === "es" ? "Nuevo caso" : "New case"}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="kpi" data-testid="metric-cases">
            <div className="kl">{lang === "es" ? "Casos activos" : "Active cases"}</div>
            <div className="kv">{metrics.active_cases}</div>
          </div>
          <div className="kpi" data-testid="metric-alerts">
            <div className="kl">{lang === "es" ? "Alertas" : "Alerts"}</div>
            <div className="kv" style={{ color: metrics.alerts > 0 ? "#854f0b" : undefined }}>{metrics.alerts}</div>
          </div>
          <div className="kpi" data-testid="metric-reports">
            <div className="kl">{lang === "es" ? "Reportes por aprobar" : "Reports to approve"}</div>
            <div className="kv">{metrics.pending_reports}</div>
          </div>
        </div>

        <p className="lbl mt-6">
          {lang === "es" ? "Casos · toca uno para abrir el expediente" : "Cases · tap to open"}
        </p>
        {loading ? (
          <p className="text-tert text-[12px] font-mono uppercase">Cargando…</p>
        ) : items.length === 0 ? (
          <div className="card">
            <p className="text-[14px]">
              {lang === "es" ? "No hay casos aún. Crea el primero." : "No cases yet. Create the first one."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((c) => (
              <div key={c.id} className="card flex items-center justify-between gap-3 hover:border-teal transition" data-testid={`case-row-${c.id}`}>
                <button
                  onClick={() => nav(`/app/circulo/child/${c.id}`)}
                  className="flex-1 flex items-center gap-3 text-left"
                  data-testid={`case-open-${c.id}`}
                >
                  <div className="w-11 h-11 rounded-full bg-teal-deep text-white flex items-center justify-center font-display text-[15px] font-medium">
                    {initials(c.name)}
                  </div>
                  <div>
                    <p className="font-display text-[17px] font-medium">{c.name}</p>
                    <p className="text-tert text-[12.5px]">{c.age} años · {c.grade || "—"} · {c.condition || "—"}</p>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-teal text-[12.5px] font-medium hidden sm:inline">{lang === "es" ? "Expediente" : "File"} →</span>
                  <button
                    className="btn-ghost btn text-[11px] px-2 py-1 text-errtext"
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                    data-testid={`case-delete-${c.id}`}
                    title={lang === "es" ? "Eliminar caso" : "Delete case"}
                  >
                    <IconTrash size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await api.delete(`/circulo/children/${deleteTarget.id}`);
          setDeleteTarget(null);
          await reload();
        }}
        title={lang === "es" ? "Eliminar caso del círculo" : "Delete circle case"}
        description={
          deleteTarget
            ? (lang === "es"
                ? `Se eliminará el expediente de <b>${deleteTarget.name}</b> junto con su bitácora, kit sensorial, accesos y reportes. Esta acción es irreversible.`
                : `The file for <b>${deleteTarget.name}</b> plus bitácora, sensory kit, accesses and reports will be removed. This is irreversible.`)
            : ""
        }
        testId="circulo-case-delete-modal"
      />
    </>
  );
}
